<script lang="ts" setup>
import type { Project } from '@factoryfixinc/ats-interfaces';
import { JobUtil } from '@/utils/job.util';
import { computed, nextTick, ref, watch } from 'vue';
import sanitizeHtml from 'sanitize-html';

import ProjectDescriptionEmailForwarding from './ProjectDescriptionEmailForwarding.vue';
import ProjectDescriptionScreeningDetails from './ProjectDescriptionScreeningDetails.vue';
import type { ProjectDescriptionJob } from './project-description-screening.utils';

const COLLAPSED_DESCRIPTION_MAX_HEIGHT_PX = 320;

const props = defineProps<{
  job: ProjectDescriptionJob;
  project?: Project;
}>();

const location = computed(() => JobUtil.getLocation(props.job));
const shift = computed(() => JobUtil.getShift(props.job));
const hireType = computed(() => JobUtil.getHireType(props.job));
const payRange = computed(() => JobUtil.getPayRange(props.job));
const sanitizedDescription = computed(() =>
  sanitizeHtml(props.job.description ?? '', {
    allowedTags: sanitizeHtml.defaults.allowedTags,
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      a: ['href', 'name', 'target', 'rel'],
    },
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    transformTags: {
      a: sanitizeHtml.simpleTransform('a', {
        rel: 'noopener noreferrer',
        target: '_blank',
      }),
    },
  }),
);
const descriptionBody = ref<HTMLElement>();
const isDescriptionExpanded = ref(false);
const canExpandDescription = ref(false);

const descriptionBodyStyle = computed(() => {
  if (isDescriptionExpanded.value || !canExpandDescription.value) {
    return undefined;
  }

  return {
    maxHeight: `${COLLAPSED_DESCRIPTION_MAX_HEIGHT_PX}px`,
  };
});

async function updateDescriptionOverflowState() {
  await nextTick();

  const descriptionElement = descriptionBody.value;
  if (!descriptionElement) {
    canExpandDescription.value = false;
    return;
  }

  canExpandDescription.value =
    descriptionElement.scrollHeight > COLLAPSED_DESCRIPTION_MAX_HEIGHT_PX + 1;
}

function toggleDescriptionExpanded() {
  isDescriptionExpanded.value = !isDescriptionExpanded.value;
}

watch(
  () => props.job.description,
  async () => {
    isDescriptionExpanded.value = false;
    await updateDescriptionOverflowState();
  },
  { immediate: true },
);
</script>

<template>
  <div>
    <div class="mb-4 flex-col text-xs">
      <div><span class="font-bold">Location: </span> {{ location }}</div>
      <div><span class="font-bold">Hire type: </span> {{ hireType }}</div>
      <div><span class="font-bold">Shift: </span> {{ shift }}</div>
      <div><span class="font-bold">Pay Range: </span> {{ payRange }}</div>
    </div>

    <div class="relative">
      <div
        ref="descriptionBody"
        v-html="sanitizedDescription"
        class="prose overflow-hidden break-words"
        :class="{
          'project-description-collapsed': canExpandDescription && !isDescriptionExpanded,
        }"
        :style="descriptionBodyStyle"
        data-test-id="project-description-body"
      />

      <div
        v-if="canExpandDescription && !isDescriptionExpanded"
        class="project-description-fade pointer-events-none absolute inset-x-0 bottom-0 h-12"
      />
    </div>

    <button
      v-if="canExpandDescription"
      type="button"
      class="mt-3 text-xs font-bold text-highlight-500 hover:underline"
      data-test-id="project-description-toggle"
      @click="toggleDescriptionExpanded"
    >
      {{ isDescriptionExpanded ? 'Show less' : 'Read more…' }}
    </button>

    <ProjectDescriptionScreeningDetails :job="job" :project="project" />

    <ProjectDescriptionEmailForwarding :job-id="job.id" />
  </div>
</template>

<style scoped>
.project-description-collapsed {
  position: relative;
}

.project-description-fade {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0), #ffffff 80%);
}
</style>
